import BenchlingConfig from 'BenchlingConfig';
import {gauge} from 'metrics/Metrics';
import {Aggregation, PerfId, PERF_ID_DISABLED, start} from 'performance/Perf';
import {addRatePreprocessors, createNewRate, recordDuration} from 'performance/RateWatcher';

const SYNC_PERF_INTERVAL_MINUTES = 1;
const SYNC_PERF_RATE_INTERVAL_SECONDS = 60;

export function startReduxSyncPerf(syncerName: string): SyncerPerfData {
  if (!BenchlingConfig.ENABLE_SYNCER_METRICS) {
    return {EqualityId: PERF_ID_DISABLED, WriteId: PERF_ID_DISABLED, SyncerName: syncerName};
  }

  return {
    EqualityId: startSingleSyncPerf('sync_equality', syncerName, 'redux'),
    WriteId: startSingleSyncPerf('sync_write', syncerName, 'redux'),
    SyncerName: syncerName,
  };
}

export function startApolloSyncPerf(syncerName: string): PerfId {
  return BenchlingConfig.ENABLE_SYNCER_METRICS
    ? startSingleSyncPerf('sync_write', syncerName, 'apollo')
    : PERF_ID_DISABLED;
}

export function reportInputSize(syncerName: string, inputs: Object): void {
  if (BenchlingConfig.ENABLE_SYNCER_SIZE_METRICS) {
    const size = getKeyCountRecursive(inputs);
    gauge('sync_slice_size', size, {syncer: syncerName});
  }
}

function startSingleSyncPerf(operationName: string, syncerName: string, syncerSource: string): PerfId {
  const tags = {syncer: syncerName, source: syncerSource};
  const individualRateId = createNewRate(
    `syncer_impact.${operationName}.${syncerSource}.per_syncer`,
    {syncer: syncerName},
    SYNC_PERF_RATE_INTERVAL_SECONDS * 1000
  );
  addRatePreprocessors('syncer_impact');
  const rateDurationCb = (duration: number): void => {
    recordDuration(individualRateId, duration);
  };
  return start(
    operationName,
    {enabled: false},
    {
      interval: SYNC_PERF_INTERVAL_MINUTES * 60 * 1000,
      aggregations: [Aggregation.COUNT, Aggregation.MEDIAN, Aggregation.MAX],
    },
    tags,
    [rateDurationCb]
  );
}

function getKeyCountRecursive(value: {[key: string]: Object} | null | Object): number {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const castValue = value as {[key: string]: Object};
    return (
      1 +
      Object.keys(value)
        .map((key) => getKeyCountRecursive(castValue[key]))
        .reduce((a, b) => a + b)
    );
  } else if (value && Array.isArray(value)) {
    return value.map((item) => getKeyCountRecursive(item)).reduce((a, b) => a + b) + 1;
  } else {
    return 1;
  }
}

export interface SyncerPerfData {
  EqualityId: PerfId;
  WriteId: PerfId;
  SyncerName: string;
}
